import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  forwardRef,
  useImperativeHandle,
} from 'react';

import apiConfig from '../../config/apiConfig';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchForeignResource } from '../../apis/resources';
import { fetchEnum } from '../../apis/enum';
import { useParams } from 'react-router-dom';

import { useRaspStore } from '../../store/raspStore';
import { useOlympiadViewModel } from '../../viewModels/useOlympiadViewModel';
import { LoginContext } from '../../context/LoginContext';
import { apiFetch } from '../../apis/apiFetch';

import './FormStyles.css';
import { BTechApplicationFields } from '../../config/btechApplicationFields';
import { validate } from '../../utils/validator';
import { btechOlympiadApplicationRules, btechOlympiadRequiredApplicationRules } from '../../validators/applicantValidator';
import RenderImage from '../QueryManager/RenderImage/RenderImage';
import { useAdmissionDriveKind } from '../../hooks/useAdmissionDriveKind';
import { useStudentProgress } from '../../context/StudentProgressContext';
// import { olympiadApplicationRules } from '../../validators/applicantValidator';

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

export interface OlympiadFormRef {
  getData: () => Record<string, unknown>;
  validate: () => Record<string, string>;
}

const F = BTechApplicationFields.olympiad;

export type CreateOlympiadProps = {
  variant?: 'standalone' | 'application';
  initialApplicationRow?: Record<string, any>;
};

const OlympiadApplicationFields = forwardRef<
  OlympiadFormRef,
  { initialApplicationRow?: Record<string, any> }
>((props, ref) => {
  const { initialApplicationRow } = props;
    const { isDriveEditOpen, isDriveOpen } = useAdmissionDriveKind();
    const { isAppEdit, isAppSubmitted } = useStudentProgress();
  
    const isEnabled = isDriveOpen || !(isDriveEditOpen && isAppSubmitted) || isAppEdit 

  const [name, setName] = useState<string | null>('');
  const [regno, setRegno] = useState('');
  const [year, setYear] = useState('');
  const [proof, setProof] = useState<File | string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    const row = initialApplicationRow;
    if (!row) return;

    // Auto-check if incoming data is populated
    if (!userInteractedRef.current) {
        const hasData = row[F.name] || row[F.no] || row[F.year] || row[F.proof];
        if (hasData) setShowForm(true);
    }

    setName(row[F.name] || '');
    setRegno(row[F.no] || '');
    setYear(row[F.year] || '');

    const p = row[F.proof];
    setProof(p instanceof File ? p : typeof p === 'string' ? p : null);
  }, [initialApplicationRow]);

  const buildPayload = (): Record<string, unknown> => ({
    [F.name]: showForm ? name || null : null,
    [F.no]: showForm ? regno || null : null,
    [F.year]: showForm ? year || null : null,
    [F.proof]: showForm ? proof : null,
  });


  useImperativeHandle(ref, () => ({
    getData: () => buildPayload(),
    validate: () => {
      if (!showForm) return {};
      const payload = buildPayload();
      const vErrors = validate(payload, btechOlympiadRequiredApplicationRules);
      setErrors(vErrors);
      return vErrors;
    },
  }));

  return (
    <div className="form-container">
      <div className="form-subtitle-row" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
        <h3 className="form-subtitle" style={{ marginBottom: 0 }}>Olympiad (Optional)</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 8px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
          <input
            type="checkbox"
            id="olympiad-toggle"
            checked={showForm}
            onChange={(e) => {
              userInteractedRef.current = true;
              setShowForm(e.target.checked);
              if (!e.target.checked) setErrors({});
            }}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <label htmlFor="olympiad-toggle" style={{ fontSize: '14px', fontWeight: 600, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
            Selected for Olympiad training camps in 2025 or 2026
          </label>
        </div>
      </div>

      <div className={`form-grid ${!showForm ? 'form-disabled' : ''}`} style={{ opacity: showForm ? 1 : 0.6 }}>
        {/* Name */}
        <div className="form-group">
          <label className="form-label">Olympiad Training Camp Name {showForm && <span style={{ color: "red" }}>*</span>}</label>
          <select
            className={`form-select ${errors[F.name] ? 'input-error' : ''}`}
            value={name || ""}
            disabled={!showForm || !isEnabled}
            style={{ cursor: (!showForm || !isEnabled) ? "not-allowed" : "default" }}
            onChange={(e) => {
              setName(e.target.value === '' ? null : e.target.value);
              if (errors[F.name]) {
                setErrors((prev) => ({ ...prev, [F.name]: '' }));
              }
            }}
          >
            <option value="">Select Olympiad Training Camp Name</option>
            <option value="International Mathematical Olympiad Training Camp (IMOTC)">International Mathematical Olympiad Training Camp (IMOTC)</option>
            <option value="International Olympiad in Informatics Training Camp (IOITC)">International Olympiad in Informatics Training Camp (IOITC)</option>
          </select>
          {errors[F.name] && (
            <span className="error-text">{errors[F.name]}</span>
          )}
        </div>

        {/* Reg No */}
        <div className="form-group">
          <label className="form-label">Hall Ticket Number {showForm && <span style={{ color: "red" }}>*</span>}</label>
          <input
            type="text"
            className={`form-input ${errors[F.no] ? 'input-error' : ''}`}
            value={regno}
            disabled={!showForm || !isEnabled}
            style={{ cursor: (!showForm || !isEnabled) ? "not-allowed" : "default" }}
            onChange={(e) => {
              setRegno(e.target.value);
              if (errors[F.no]) {
                setErrors((prev) => ({ ...prev, [F.no]: '' }));
              }
            }}
          />
          {errors[F.no] && (
            <span className="error-text">{errors[F.no]}</span>
          )}
        </div>

        {/* Year */}
        <div className="form-group">
          <label className="form-label">Year of Olympiad {showForm && <span style={{ color: "red" }}>*</span>}</label>
          <select
            className={`form-select ${errors[F.year] ? 'input-error' : ''}`}
            value={year}
            disabled={!showForm || !isEnabled}
            style={{ cursor: (!showForm || !isEnabled) ? "not-allowed" : "default" }}
            onChange={(e) => {
              setYear(e.target.value);
              if (errors[F.year]) {
                setErrors((prev) => ({ ...prev, [F.year]: '' }));
              }
            }}
          >
            <option value="">Select Year</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
          {errors[F.year] && (
            <span className="error-text">{errors[F.year]}</span>
          )}
        </div>

        {/* Proof */}
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <>
            <label className="form-label">Proof of Olympiad Training {showForm && <span style={{ color: "red" }}>*</span>}</label>

            <div className="file-upload-wrapper">
              <input
                className={`form-input file-input ${errors[F.proof] ? "input-error" : ""}`}
                type="file"
                disabled={!showForm || !isEnabled}
                style={{ cursor: (!showForm || !isEnabled) ? "not-allowed" : "default" }}
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;

                  // ✅ File size validation (512 KB)
                  if (file && file.size > 512 * 1024) {
                    setPhotoError(
                      "File size must be less than 512 KB. Please choose a smaller file."
                    );
                    e.target.value = "";
                    return;
                  }

                  setPhotoError("");

                  // ✅ Update state
                  setProof(file);

                  // ✅ Generate preview
                  if (file) {
                    const objectUrl = URL.createObjectURL(file);
                    setPreview(objectUrl);
                  }

                  // ✅ Clear validation error
                  if (errors[F.proof]) {
                    setErrors((prev) => ({
                      ...prev,
                      [F.proof]: "",
                    }));
                  }
                }}
              />
            </div>

            {/* ✅ File size error */}
            {photoError && (
              <span
                style={{
                  color: "red",
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {photoError}
              </span>
            )}

            {/* ✅ Validation error */}
            {errors[F.proof] && (
              <span className="error-text">{errors[F.proof]}</span>
            )}

            {/* ✅ Helper text */}
            <small
              style={{
                color: "#6b7280",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Max size: 512 KB | Formats: JPG, JPEG, PNG, PDF
            </small>
          </>

          {/* ✅ Preview OR Existing file */}
          {preview ? (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginLeft: 100,
                marginTop: 10,
              }}
            >
              <img src={preview} alt="preview" style={{ maxWidth: "40%" }} />
            </div>
          ) : typeof proof === "string" && proof ? (
            <div>
              <small
                style={{
                  color: "#1d1e1f",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Previous Uploaded File:
              </small>

              <RenderImage photo={proof} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

OlympiadApplicationFields.displayName = 'OlympiadApplicationFields';

function CreateOlympiad(
  props: CreateOlympiadProps,
  ref: React.ForwardedRef<OlympiadFormRef>
) {
  const { variant = 'standalone', initialApplicationRow } = props;

  if (variant === 'application') {
    return (
      <OlympiadApplicationFields
        ref={ref}
        initialApplicationRow={initialApplicationRow}
      />
    );
  }

  return <OlympiadStandalone />;
}

const OlympiadStandalone = () => {
  const { user } = useContext(LoginContext);

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const metadataUrl = apiConfig.getResourceMetaDataUrl('Olympiad');

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
  } = useOlympiadViewModel(user?.userId || '', appId);

  useEffect(() => {
    loadMetadata();
  }, []);

  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  useQuery({
    queryKey: ['resMetaData', 'OlympiadCreate'],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter((field: any) => field.foreign);

      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ['foreignData', field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(field.foreign, field.name, field.foreign_field);
        }
      }

      const enumFields = data[0].fieldValues.filter((field: any) => field.isEnum === true);

      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ['enum', field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });

  useQuery({
    queryKey: ['resourceData', 'OlympiadCreate', localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      const activeDriveId = localStorage.getItem("activeDriveId");
      if (!activeDriveId) return [];

      const params = new URLSearchParams();
      params.append('queryId', 'GET_ALL'); // Note: This might need a GET_BY_USER_ID like others

      const response = await apiFetch(
        `${apiConfig.getResourceUrl('Olympiad')}?` + params.toString(),
        {
          method: 'GET',
        }
      );

      if (!response.ok) {
        throw new Error('Error: ' + response.status);
      }

      const data = await response.json();
      // Even if GET_ALL is used for some reason, filter it manually for safety
      return (data.resource || []).filter((r: any) => r.drive_id === activeDriveId);
    },
  });

  return (
    <div className="form-container">
      <h3 className="form-subtitle">Olympiad Details</h3>

      <div className="form-grid">
        {/* Name */}
        <div className="form-group">
          <label className="form-label">Olympiad Name</label>
          <select
            className="form-select"
            value={dataToSave[F.name] || ''}
            onChange={(e) =>
              setDataToSave({ ...dataToSave, [F.name]: e.target.value === '' ? null : e.target.value })
            }
          >
            <option value="">Select Olympiad</option>
            <option value="International Mathematical Olympiad Training Camp (IMOTC)">International Mathematical Olympiad Training Camp (IMOTC)</option>
            <option value="International Olympiad in Informatics Training Camp (IOITC)">International Olympiad in Informatics Training Camp (IOITC)</option>
          </select>
        </div>

        {/* Reg No */}
        <div className="form-group">
          <label className="form-label">Registration Number</label>
          <input
            type="text"
            className="form-input"
            value={dataToSave[F.no] || ''}
            onChange={(e) =>
              setDataToSave({ ...dataToSave, [F.no]: e.target.value })
            }
          />
        </div>

        {/* Year */}
        <div className="form-group">
          <label className="form-label">Year of Olympiad</label>
          <select
            className="form-select"
            value={dataToSave[F.year] || ''}
            onChange={(e) =>
              setDataToSave({ ...dataToSave, [F.year]: e.target.value })
            }
          >
            <option value="">Select Year</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
        </div>

        {/* Proof */}
        <div className="form-group">
          <label className="form-label">Proof Document</label>
          <div className="file-upload-wrapper">
            <input
              className="form-input file-input"
              type="file"
              onChange={(e) =>
                setDataToSave({
                  ...dataToSave,
                  [F.proof]: e.target.files?.[0] || null,
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateOlympiadExport = forwardRef(CreateOlympiad);
CreateOlympiadExport.displayName = 'CreateOlympiad';

export default CreateOlympiadExport;